import { useOutletContext, useParams } from "react-router-dom";
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import MetricGrid from "@/components/MetricGrid";
import Table, { TableHeading, TableFooter } from "@/components/Table";
// import Button from "@/components/Button";
import EmptyState from "@/components/EmptyState";
import { buildColumns } from "./_utils";
import { Student } from "../../types";
import { StudentTestResult } from "./types";
import { GET_UNIVERSITY_STUDENTS_WITH_TEST_SCORES } from "../../../test-results/queries";

export default function StudentTestResultsPage() {
  const { universityId } = useParams();
  const { student } = useOutletContext<{ student: Student }>();

  const { data, loading } = useQuery(GET_UNIVERSITY_STUDENTS_WITH_TEST_SCORES, {
    variables: {
      input: {
        universityLegacyIds: [universityId?.toString() || ""],
        alumni: false,
        search: student.pgaId
      }
    }
  });

  const testResultsData: StudentTestResult[] = useMemo(() => {
    if (!data?.universityStudents?.nodes) return [];

    const allTestResults: StudentTestResult[] = [];

    for (const studentData of data.universityStudents.nodes) {
      const testHistory = studentData.educationTestHistory || [];

      for (const testResult of testHistory) {
        allTestResults.push({
          id: `${studentData.id}-${testResult.testId}-${testResult.attemptNumber}`,
          testId: testResult.testId,
          testName: testResult.testDefinition?.description || testResult.testId,
          level: testResult.levelCode,
          testDate: testResult.testDate,
          isRetake: testResult.isRetake,
          score: testResult.score,
          maxScore: testResult.maxScore,
          passed: testResult.isPassed,
          student: {
            id: studentData.id,
            firstName: studentData.firstName,
            lastName: studentData.lastName,
            pgaId: studentData.pgaId,
            email: ""
          }
        });
      }
    }

    return allTestResults;
  }, [data]);

  const testResultsMetrics = useMemo(() => {
    const levelGroups = testResultsData.reduce(
      (acc, result) => {
        const level = result.level;
        if (!acc[level]) {
          acc[level] = { passed: 0, total: 0 };
        }
        acc[level].total++;
        if (result.passed) {
          acc[level].passed++;
        }
        return acc;
      },
      {} as Record<string, { passed: number; total: number }>
    );

    const totalPassed = testResultsData.filter((result) => result.passed).length;
    const totalTests = testResultsData.length;

    const levelMetrics = Object.entries(levelGroups)
      .filter(([_, data]) => data.total > 0)
      .map(([level, data]) => {
        const levelNumber = level.match(/\d+$/)?.[0] || level;
        return {
          label: `Level ${levelNumber} Tests Passed`,
          value: `${data.passed}/${data.total}`,
          iconName: "description" as const,
          variant: data.passed === data.total ? ("success" as const) : undefined
        };
      });

    const metrics =
      totalTests > 0
        ? [
            ...levelMetrics,
            {
              label: "Total Tests Passed",
              value: `${totalPassed}/${totalTests}`,
              iconName: "description" as const,
              variant: totalPassed === totalTests ? ("success" as const) : undefined
            }
          ]
        : [];

    return metrics;
  }, [testResultsData]);

  const columns = useMemo(() => buildColumns(), []);

  return (
    <>
      {testResultsMetrics.length > 0 && <MetricGrid metrics={testResultsMetrics} layout="expanded" />}

      <TableHeading
        heading="PGM Program Test Results"
        body="View all test results associated with this student."
        actions={[
          // {
          //   label: "Export Test Results",
          //   icon: "download" as const,
          //   variant: "text" as const,
          //   onClick: () => {},
          //   testId: "export-test-results-button"
          // }
        ]}
      />
      <Table
        data={testResultsData}
        columns={columns}
        loading={loading}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Test Results" caption="There are no records to display at this time" iconName="quiz" />
        )}
        renderFooter={() => <TableFooter totalRowCount={testResultsData.length} />}
      />
    </>
  );
}
